import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Table } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import CompanyContext from '../../auth/CompanyContext';
import Loading from '../Loading/Loading';
import * as utilities from '../../utilities';
import './Contacts2.css';

const normalizeValue = (value) => String(value || '').trim().toLowerCase();
const TOP_HOLDINGS_LIMIT = 5;

const ContactsInvestments2 = ({
    idToken,
    isActive,
    selectedContactHouseholds = [],
    contactAccountsLoading = false,
    householdRows = [],
    householdsLoading = false,
    companySettings
}) => {
    const companyCtx = useContext(CompanyContext);
    const hasCompanyPortalDatabase = String(companySettings?.CompanyPortalDatabase || '').trim() !== '';

    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [expandedAccounts, setExpandedAccounts] = useState({});

    const selectedHouseholdKeys = useMemo(() => (
        (selectedContactHouseholds || [])
            .map(item => String(item || '').trim())
            .filter(Boolean)
    ), [selectedContactHouseholds]);

    const householdLookup = useMemo(() => {
        return (householdRows || []).reduce((acc, row) => {
            const household = row?.household || row?.householdName || '';
            if (!household) return acc;
            const key = normalizeValue(household);
            if (!acc[key]) acc[key] = [];
            acc[key].push(row);
            return acc;
        }, {});
    }, [householdRows]);

    const toNumber = useCallback((value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    }, []);

    const formatCurrency = useCallback((value) => {
        return toNumber(value).toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }, [toNumber]);

    const formatPercent = useCallback((value) => {
        const parsed = Number(value);
        if (!Number.isFinite(parsed)) return '';
        const normalized = Math.abs(parsed) <= 1 ? parsed * 100 : parsed;
        return `${normalized.toFixed(2)}%`;
    }, []);

    const formatDate = useCallback((value) => {
        if (!value) return '';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '';
        return date.toLocaleDateString('en-US', { timeZone: 'UTC' });
    }, []);

    const getAccountLabel = useCallback((household) => {
        const rows = householdLookup[normalizeValue(household)] || [];
        const firstRow = rows[0] || {};
        const displayName = String(firstRow.displayName || '').replace(` - ${firstRow.account || ''}`, '').trim();
        return displayName || firstRow.householdName || firstRow.household || household || 'Account';
    }, [householdLookup]);

    const getHoldingLabel = useCallback((holding = {}) => {
        return holding.securityName
            || holding.SecurityName
            || holding.securityDescription
            || holding.SecurityDescription
            || holding.assetAllocation
            || holding.AssetAllocation
            || holding.sector
            || holding.Sector
            || holding.securitySymbol
            || holding.SecuritySymbol
            || holding.symbol
            || holding.Symbol
            || 'Holding';
    }, []);

    const getHoldingMarketValue = useCallback((holding = {}) => (
        toNumber(
            holding.marketValue
            ?? holding.MarketValue
            ?? holding.market_value
            ?? holding.value
            ?? holding.Value
        )
    ), [toNumber]);

    const getHoldingPercentage = useCallback((holding = {}, totalMarketValue = 0) => {
        const explicit = holding.percentage ?? holding.Percentage ?? holding.percent ?? holding.Percent;
        if (explicit != null && explicit !== '') return explicit;
        const holdingValue = getHoldingMarketValue(holding);
        if (!totalMarketValue) return '';
        return holdingValue / totalMarketValue;
    }, [getHoldingMarketValue]);

    const getLatestHoldingsDate = useCallback(async (household) => {
        const results = await fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/posDates`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + (idToken || ''),
                'X-Database': companyCtx.company
            },
            body: JSON.stringify({
                port: '',
                household
            })
        });
        if (results.status === 401 || results.status === 403) throw new Error('Portal+ authorization failed');
        const response = await results.json();
        if (response.code === 200 && response.data && response.data.length > 0) return response.data[0].fDate;
        return '';
    }, [idToken, companyCtx.company]);

    const getHoldings = useCallback(async (household, dateValue) => {
        const dateObj = dateValue ? new Date(dateValue) : null;
        const params = new URLSearchParams({
            household,
            date: dateObj && !Number.isNaN(dateObj.getTime()) ? dateObj.toISOString() : ''
        });
        const results = await fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/holdingsSnapshot?${params.toString()}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + (idToken || ''),
                'X-Database': companyCtx.company
            }
        });
        if (results.status === 401 || results.status === 403) throw new Error('Portal+ authorization failed');
        const response = await results.json();
        if (response.code !== 200) return [];
        return response.data || [];
    }, [idToken, companyCtx.company]);

    const loadInvestments = useCallback(async () => {
        if (!isActive) return;
        if (!hasCompanyPortalDatabase || !idToken || !companyCtx.company) {
            setAccounts([]);
            return;
        }
        if (contactAccountsLoading || householdsLoading) return;
        if (selectedHouseholdKeys.length === 0) {
            setAccounts([]);
            return;
        }

        setLoading(true);
        setError('');

        try {
            const nextAccounts = await Promise.all(selectedHouseholdKeys.map(async (household) => {
                const date = await getLatestHoldingsDate(household);
                const holdings = await getHoldings(household, date);
                const totalMarketValue = holdings.reduce((sum, holding) => sum + getHoldingMarketValue(holding), 0);
                const sortedHoldings = [...holdings].sort((a, b) => (
                    Math.abs(getHoldingMarketValue(b)) - Math.abs(getHoldingMarketValue(a))
                ));
                return {
                    household,
                    name: getAccountLabel(household),
                    date,
                    marketValue: totalMarketValue,
                    holdings: sortedHoldings
                };
            }));
            setAccounts(nextAccounts);
        } catch (err) {
            setAccounts([]);
            setError('Unable to load investment data from Portal+.');
            utilities.addToast('Unable to load investment data from Portal+.', 5, 'danger');
        } finally {
            setLoading(false);
        }
    }, [
        isActive,
        hasCompanyPortalDatabase,
        idToken,
        companyCtx.company,
        contactAccountsLoading,
        householdsLoading,
        selectedHouseholdKeys,
        getLatestHoldingsDate,
        getHoldings,
        getHoldingMarketValue,
        getAccountLabel
    ]);

    useEffect(() => {
        loadInvestments();
    }, [loadInvestments]);

    const toggleExpanded = useCallback((household) => {
        setExpandedAccounts(prev => ({
            ...prev,
            [household]: !prev[household]
        }));
    }, []);

    if (!isActive) return null;

    if (!hasCompanyPortalDatabase) {
        return <Alert color="light">This company is not configured for Portal+ investment data.</Alert>;
    }

    if (contactAccountsLoading || householdsLoading || loading) {
        return <Loading />;
    }

    if (selectedHouseholdKeys.length === 0) {
        return <Alert color="light">No Portal+ accounts are mapped to this contact yet. Use Account Mappings to select the contact&apos;s portfolio accounts.</Alert>;
    }

    if (error) {
        return <Alert color="danger">{error}</Alert>;
    }

    if (accounts.length === 0) {
        return <Alert color="light">No investment holdings were returned for this contact&apos;s mapped accounts.</Alert>;
    }

    const totalMarketValue = accounts.reduce((sum, account) => sum + toNumber(account.marketValue), 0);

    return (
        <div className="contactsInvestments text-left">
            <Table size="sm" responsive className="contactsInvestmentsTable">
                <thead>
                    <tr>
                        <th className="contactsInvestmentsAccountCol">Account</th>
                        <th className="contactsInvestmentsAsOfCol">As Of</th>
                        <th className="contactsInvestmentsTopHoldingsCol">Holdings</th>
                        <th className="contactsInvestmentsMarketValueCol">Market Value</th>
                    </tr>
                </thead>
                <tbody>
                    {accounts.map(account => {
                        const isExpanded = !!expandedAccounts[account.household];
                        const displayedHoldings = isExpanded
                            ? account.holdings
                            : account.holdings.slice(0, TOP_HOLDINGS_LIMIT);
                        const hasAdditionalHoldings = account.holdings.length > TOP_HOLDINGS_LIMIT;
                        return (
                            <React.Fragment key={account.household}>
                                <tr>
                                    <td className="contactsInvestmentsAccountCol">
                                        <div className="font-weight-bold">{account.name}</div>
                                        <div className="text-muted small">{account.household}</div>
                                    </td>
                                    <td className="contactsInvestmentsAsOfCol">{formatDate(account.date)}</td>
                                    <td className="contactsInvestmentsTopHoldingsCol">
                                        {displayedHoldings.length > 0 ? (
                                            <ol className="contactsInvestmentsTopHoldings">
                                                {displayedHoldings.map((holding, index) => (
                                                    <li key={`${getHoldingLabel(holding)}-${index}`}>
                                                        {getHoldingLabel(holding)} <span className="text-muted">({formatCurrency(getHoldingMarketValue(holding))} / {formatPercent(getHoldingPercentage(holding, account.marketValue))})</span>
                                                    </li>
                                                ))}
                                            </ol>
                                        ) : (
                                            <span className="text-muted">No holdings</span>
                                        )}
                                        {hasAdditionalHoldings && (
                                            <Button color="link" className="contactsInvestmentsToggle d-block mt-1" onClick={() => toggleExpanded(account.household)}>
                                                <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} className="mr-1" />
                                                {isExpanded ? 'Hide Holdings' : 'View Holdings'}
                                            </Button>
                                        )}
                                    </td>
                                    <td className="contactsInvestmentsMarketValueCol">{formatCurrency(account.marketValue)}</td>
                                </tr>
                            </React.Fragment>
                        );
                    })}
                    {accounts.length >= 2 && (
                        <tr className="contactsInvestmentsTotalRow">
                            <td className="contactsInvestmentsAccountCol font-weight-bold">Total</td>
                            <td className="contactsInvestmentsAsOfCol"></td>
                            <td className="contactsInvestmentsTopHoldingsCol"></td>
                            <td className="contactsInvestmentsMarketValueCol font-weight-bold">{formatCurrency(totalMarketValue)}</td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </div>
    );
};

export default ContactsInvestments2;
